import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ArrowUp, Calendar as CalendarIcon, ChevronLeft, ChevronRight, Eye, EyeOff, Filter, LayoutGrid, List, X } from 'lucide-react';
import { CalendarEvent } from '../types/calendar';
import { calendarApi } from '../services/calendarApi';
import api from '../lib/api';
import { 
  formatEventDate, 
  isCommitteeEvent,
  CalendarEventDisplay
} from '../utils/calendarUtils';
import { toLocalDateString } from '../utils/dateUtils';
import { useOrganizations } from '../hooks/useOrganizations';
import { useMobile } from '../hooks/useMediaQuery';
import ShareCalendarDropdown from '../components/ShareCalendarDropdown';
import SearchField from '../components/SearchField';
import CalendarDayView from '../components/calendar/CalendarDayView';
import MiniMonthCalendar from '../components/calendar/MiniMonthCalendar';
import LoadingSpinner from '@shared/ui/LoadingSpinner';
import Modal from '@shared/ui/Modal';
import PageShell from '../components/PageShell';
import StatCard, { StatCardRow } from '../components/StatCard';
import ExportMenu from '../components/ExportMenu';
import ViewToggle from '../components/ViewToggle';
import EmptyState from '../components/EmptyState';

interface CalendarDayData {
  date: string;
  events: Array<CalendarEvent & { bills: Array<{ bill_id: string; bill_order: number }> }>;
  total_events: number;
  total_bills: number;
  summary: {
    total_bills: number;
    senate_chamber_hearings: number;
    senate_committee_hearings: number;
    house_chamber_hearings: number;
    house_committee_hearings: number;
    total_events: number;
  };
}

type ViewMode = 'cards' | 'table';
type SortField = 'time' | 'title' | 'chamber' | 'bills';
type SortDirection = 'asc' | 'desc';

interface TableFilters {
  chamber: 'all' | 'senate' | 'house' | 'joint';
  eventType: 'all' | 'committee' | 'chamber';
  search: string;
}

interface ColumnVisibility {
  time: boolean;
  event: boolean;
  chamber: boolean;
  location: boolean;
  bills: boolean;
}

// Helper functions for table operations
const sortEvents = (events: CalendarEvent[], field: SortField, direction: SortDirection): CalendarEvent[] => {
  if (!events || !Array.isArray(events)) {
    return [];
  }
  
  return [...events].sort((a, b) => {
    // Safety check for event objects
    if (!a || !b) return 0;
    
    let aValue: string | number;
    let bValue: string | number;
    
    try {
      switch (field) {
        case 'time':
          // Handle special time cases
          if (a.no_time || a.upon_adjournment) aValue = '99:99';
          else aValue = a.reading_time || '00:00';
          
          if (b.no_time || b.upon_adjournment) bValue = '99:99';
          else bValue = b.reading_time || '00:00';
          break;
          
        case 'title':
          aValue = (a.reading_title || '').toLowerCase();
          bValue = (b.reading_title || '').toLowerCase();
          break;
          
        case 'chamber':
          aValue = a.joint_meeting ? 'joint' : (a.chamber || '').toLowerCase();
          bValue = b.joint_meeting ? 'joint' : (b.chamber || '').toLowerCase();
          break;
          
        case 'bills':
          aValue = a.bill_numbers?.length || 0;
          bValue = b.bill_numbers?.length || 0;
          break;
          
        default:
          return 0;
      }
      
      if (aValue < bValue) return direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return direction === 'asc' ? 1 : -1;
      return 0;
    } catch (error) {
      console.warn('Error sorting events:', error);
      return 0;
    }
  });
};

const filterEvents = (events: CalendarEvent[], filters: TableFilters): CalendarEvent[] => {
  if (!events || !Array.isArray(events)) {
    return [];
  }
  
  return events.filter(event => {
    // Ensure event has required properties
    if (!event || typeof event !== 'object') return false;
    
    // Chamber filter
    if (filters.chamber !== 'all') {
      if (filters.chamber === 'joint' && !event.joint_meeting) return false;
      if (filters.chamber === 'senate' && (event.joint_meeting || event.chamber?.toLowerCase() !== 'senate')) return false;
      if (filters.chamber === 'house' && (event.joint_meeting || event.chamber?.toLowerCase() !== 'house')) return false;
    }
    
    // Event type filter
    if (filters.eventType !== 'all') {
      try {
        const isCommittee = isCommitteeEvent(event);
        if (filters.eventType === 'committee' && !isCommittee) return false;
        if (filters.eventType === 'chamber' && isCommittee) return false;
      } catch (error) {
        console.warn('Error checking committee event:', error);
        return true; // Include event if we can't determine type
      }
    }
    
    // Search filter
    if (filters.search && filters.search.trim()) {
      const searchLower = filters.search.toLowerCase();
      const titleMatch = event.reading_title?.toLowerCase().includes(searchLower) || false;
      const billMatch = event.bill_numbers?.some(bill => 
        bill?.toLowerCase().includes(searchLower)
      ) || false;
      const roomMatch = event.room_number?.toLowerCase().includes(searchLower) || false;
      
      if (!titleMatch && !billMatch && !roomMatch) return false;
    }
    
    return true;
  });
};

// NOTE: linkifyBillNumbers, formatTableDate, formatTimeForDisplay, and EventNotesButton
// have been moved to the unified CalendarDayView component in components/calendar/CalendarDayView.tsx

const CalendarDetailPage: React.FC = () => {
  const { id: workspaceId, date } = useParams<{ id: string; date: string }>();
  const navigate = useNavigate();
  
  const [searchParams] = useSearchParams();
  const displayPreference = searchParams.get('display');
  const filterPreference = searchParams.get('filter');
  
  // Redirect to unified CalendarTab view
  useEffect(() => {
    if (date) {
      const [year, month, day] = date.split('-');
      const displayParam = displayPreference ? `&display=${displayPreference}` : '';
      const filterParam = filterPreference ? `&filter=${filterPreference}` : '';
      
      if (workspaceId) {
        navigate(`/workspaces/${workspaceId}?tab=calendar&year=${year}&month=${month}&day=${day}&view=day${displayParam}`, { replace: true });
      } else {
        navigate(`/calendar?year=${year}&month=${month}&day=${day}&view=day${displayParam}${filterParam}`, { replace: true });
      }
    }
  }, [date, workspaceId, displayPreference, filterPreference, navigate]);

  // Mobile detection - force cards view on mobile
  const isMobile = useMobile();
  
  // Show loading while redirecting
  const [dayData, setDayData] = useState<CalendarDayData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Use display preference from URL if provided, otherwise default to 'table'
  const [viewMode, setViewMode] = useState<ViewMode>(displayPreference === 'cards' ? 'cards' : 'table');
  // Force cards view on mobile - tables are not suitable for narrow screens
  const effectiveViewMode: ViewMode = isMobile ? 'cards' : viewMode;
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  
  // Table-specific state
  const sortField: SortField = 'time';
  const sortDirection: SortDirection = 'asc';
  const [tableFilters, setTableFilters] = useState<TableFilters>({
    chamber: 'all',
    eventType: 'all',
    search: ''
  });
  const [columnVisibility, setColumnVisibility] = useState<ColumnVisibility>(() => {
    // Default mobile-friendly column visibility
    const isMobile = window.innerWidth < 768;
    return {
      time: true,
      event: true,
      chamber: !isMobile,
      location: false, // Hide location by default as it's often empty
      bills: true
    };
  });
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [chamberFilter, setChamberFilter] = useState<'all' | 'senate' | 'house' | 'joint'>('all');

  // State for mini-calendar dots
  const [datesWithEvents, setDatesWithEvents] = useState<Set<string>>(new Set());

  // Validate date parameter and create a validated date string for TypeScript
  const isValidDate = !!(date && /^\d{4}-\d{2}-\d{2}$/.test(date));
  // Use a validated date that TypeScript knows is a string (fallback won't be used due to early return)
  const validatedDate = isValidDate ? date : '';
  const formattedDate = isValidDate ? formatEventDate(validatedDate) : '';

  // Fetch workspace details to get organization ID for sharing
  const { data: workspaceData } = useQuery({
    queryKey: ['workspace', workspaceId],
    queryFn: async () => {
      const { data } = await api.get(`/workspaces/${workspaceId}`);
      return data?.data || data;
    },
    enabled: !!workspaceId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Get user's organizations to find org admin organization for sharing
  const { organizations } = useOrganizations();
  
  // Get organization ID - prefer workspace org, fall back to user's org admin org
  const workspaceOrgId = workspaceData?.owner_org_id;
  const userOrgAdminOrg = organizations.find(org => org.role === 'org_admin');
  const organizationId = workspaceOrgId || userOrgAdminOrg?.id;

  useEffect(() => {
    if (isValidDate) {
      fetchDayData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validatedDate, workspaceId, isValidDate]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle if not typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }
      
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          navigateToPreviousDay();
          break;
        case 'ArrowRight':
          e.preventDefault();
          navigateToNextDay();
          break;
        case 'Home':
          e.preventDefault();
          navigateToToday();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validatedDate, workspaceId]);

  // Fetch month events for mini-calendar dots
  useEffect(() => {
    const fetchMonthEvents = async () => {
      if (!isValidDate) return;
      
      try {
        const currentDate = new Date(validatedDate + 'T00:00:00');
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const startDate = toLocalDateString(new Date(year, month, 1));
        const endDate = toLocalDateString(new Date(year, month + 1, 0));
        
        const response = await calendarApi.getEvents({
          start_date: startDate,
          end_date: endDate,
          workspace_id: workspaceId,
          limit: 1000
        });
        
        const dates = new Set<string>();
        response.data?.events?.forEach((event: any) => {
          const dateOnly = event.reading_date?.split('T')[0];
          if (dateOnly) dates.add(dateOnly);
        });
        setDatesWithEvents(dates);
      } catch (err) {
        console.error('Failed to fetch month events for calendar dots:', err);
      }
    };
    
    fetchMonthEvents();
  }, [validatedDate, workspaceId, isValidDate]);

  // Scroll to top function
  const scrollToTop = () => {
    // Try multiple scroll targets for better compatibility
    const scrollTargets = [
      window,
      document.documentElement,
      document.body,
      ...Array.from(document.querySelectorAll('main.overflow-y-auto'))
    ];

    scrollTargets.forEach(target => {
      if (target === window) {
        target.scrollTo({ top: 0, behavior: 'smooth' });
      } else if (target instanceof Element) {
        target.scrollTo({ top: 0, behavior: 'smooth' });
      }
    });
  };

  // Handle scroll event to show/hide scroll to top button
  useEffect(() => {
    const handleScroll = () => {
      // Check both window scroll and main container scroll
      const windowScrollTop = window.pageYOffset || document.documentElement.scrollTop;
      
      // Find the main scrollable container (the one with actual content)
      const mainContainers = document.querySelectorAll('main.overflow-y-auto');
      let containerScrollTop = 0;
      
      // Find the main container that actually has scrollable content
      for (const container of mainContainers) {
        if (container.scrollHeight > container.clientHeight && container.clientHeight > 0) {
          containerScrollTop = container.scrollTop;
          break;
        }
      }
      
      // Show button if either window or container is scrolled
      const totalScroll = Math.max(windowScrollTop, containerScrollTop);
      setShowScrollToTop(totalScroll > 300); // Show button after scrolling 300px
    };

    const setupScrollListeners = () => {
      // Add listeners to both window and main container
      window.addEventListener('scroll', handleScroll);
      
      // Find the main container that actually has scrollable content
      const mainContainers = document.querySelectorAll('main.overflow-y-auto');
      for (const container of mainContainers) {
        if (container.scrollHeight > container.clientHeight && container.clientHeight > 0) {
          container.addEventListener('scroll', handleScroll);
          return container;
        }
      }
      return null;
    };

    // Try to setup listeners immediately
    let mainContainer = setupScrollListeners();

    // If main container wasn't ready, try again after content loads
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    if (!mainContainer) {
      retryTimer = setTimeout(() => {
        mainContainer = setupScrollListeners();
      }, 1000);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (mainContainer) {
        mainContainer.removeEventListener('scroll', handleScroll);
      }
      if (retryTimer) {
        clearTimeout(retryTimer);
      }
    };
  }, [dayData]); // Re-run when dayData changes

  const fetchDayData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use workspace filtering if we're in a workspace context (workspaceId exists in URL)
      const workspaceIdParam = workspaceId ? workspaceId : undefined;
      const response = await calendarApi.getDayDetails(validatedDate, workspaceIdParam);
      
      if (response.success) {
        setDayData(response.data);
      } else {
        throw new Error('Failed to fetch calendar data');
      }
    } catch (err) {
      console.error('Failed to fetch day data:', err);
      setError('Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  };



  const handleBillClick = (billNumber: string) => {
    navigate(`/legislation/${billNumber}`);
  };

  // Table operation handlers
  const handleFilterChange = (key: keyof TableFilters, value: string) => {
    setTableFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setTableFilters({
      chamber: 'all',
      eventType: 'all',
      search: ''
    });
  };

  const toggleColumn = (column: keyof ColumnVisibility) => {
    setColumnVisibility(prev => ({ ...prev, [column]: !prev[column] }));
  };

  const hasActiveFilters = tableFilters.chamber !== 'all' || 
                          tableFilters.eventType !== 'all' || 
                          tableFilters.search !== '';

  // Apply main search and chamber filters to all events
  const searchFilteredEvents = React.useMemo(() => {
    if (!dayData?.events || !Array.isArray(dayData.events)) {
      return [];
    }
    
    return dayData.events.filter(event => {
      // Chamber filter
      if (chamberFilter !== 'all') {
        if (chamberFilter === 'joint') {
          // For joint, check if joint_meeting is true OR chamber is 'joint'
          if (!event.joint_meeting && event.chamber?.toLowerCase() !== 'joint') {
            return false;
          }
        } else {
          // For senate/house, check chamber matches
          if (event.chamber?.toLowerCase() !== chamberFilter) {
            return false;
          }
        }
      }
      
      // Search filter
      if (searchQuery.trim()) {
        const searchLower = searchQuery.toLowerCase();
        const searchableText = [
          event.reading_title,
          event.calendar_description,
          event.room_number,
          event.chamber,
          ...event.bill_numbers
        ].join(' ').toLowerCase();
        
        if (!searchableText.includes(searchLower)) {
          return false;
        }
      }
      
      return true;
    });
  }, [dayData?.events, searchQuery, chamberFilter]);

  // Process events for table view (applies table filters + sort to search-filtered events)
  const processedEvents = React.useMemo(() => {
    if (!searchFilteredEvents || !Array.isArray(searchFilteredEvents)) {
      return [];
    }
    
    try {
      const filtered = filterEvents(searchFilteredEvents, tableFilters);
      return sortEvents(filtered, sortField, sortDirection);
    } catch (error) {
      console.error('Error processing events:', error);
      return searchFilteredEvents || [];
    }
  }, [searchFilteredEvents, tableFilters, sortField, sortDirection]);

  // Compute tracked bills count for workspace view
  // Uses tracked_bills from backend and deduplicates by bill number
  const trackedBillsCount = React.useMemo(() => {
    if (!workspaceId || !dayData?.events) return 0;
    
    const trackedBillNumbers = new Set<string>();
    
    dayData.events.forEach((event: any) => {
      // Use tracked_bills directly from backend (consistent with CalendarDayView)
      const trackedBillsData = event.tracked_bills || [];
      
      trackedBillsData.forEach((bill: any) => {
        const billNumber = bill.bill_id || bill.number || bill.bill_number;
        if (billNumber) {
          trackedBillNumbers.add(billNumber);
        }
      });
    });
    
    return trackedBillNumbers.size;
  }, [workspaceId, dayData?.events]);

  // Day navigation functions
  const navigateToDay = (direction: -1 | 1) => {
    const currentDate = new Date(validatedDate);
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + direction);
    
    // Format date as YYYY-MM-DD
    const newDateString = toLocalDateString(newDate);
    
    // Use workspace-specific or general calendar route
    if (workspaceId) {
      navigate(`/workspaces/${workspaceId}/calendar/${newDateString}`);
    } else {
      navigate(`/calendar/${newDateString}`);
    }
  };

  const navigateToToday = () => {
    const today = toLocalDateString(new Date());
    
    // Use workspace-specific or general calendar route
    if (workspaceId) {
      navigate(`/workspaces/${workspaceId}/calendar/${today}`);
    } else {
      navigate(`/calendar/${today}`);
    }
  };

  const navigateToPreviousDay = () => navigateToDay(-1);
  const navigateToNextDay = () => navigateToDay(1);

  // Mini-calendar state
  const [miniCalendarMonth, setMiniCalendarMonth] = React.useState(() => {
    if (isValidDate) {
      const d = new Date(validatedDate + 'T00:00:00');
      return new Date(d.getFullYear(), d.getMonth(), 1);
    }
    return new Date();
  });

  // Mini-calendar handlers
  const handleMiniCalendarPrevMonth = () => {
    setMiniCalendarMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const handleMiniCalendarNextMonth = () => {
    setMiniCalendarMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const handleMiniCalendarSelectDate = (dateStr: string) => {
    // Navigate to the selected date while preserving display mode
    if (workspaceId) {
      navigate(`/workspaces/${workspaceId}/calendar/${dateStr}?display=${viewMode}`);
    } else {
      navigate(`/calendar/${dateStr}?display=${viewMode}`);
    }
  };

  // Get formatted dates for navigation context
  const getNavigationDates = () => {
    const currentDate = new Date(validatedDate);
    const previousDate = new Date(currentDate);
    const nextDate = new Date(currentDate);
    
    previousDate.setDate(currentDate.getDate() - 1);
    nextDate.setDate(currentDate.getDate() + 1);
    
    const formatShortDate = (d: Date) => {
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };
    
    return {
      previous: formatShortDate(previousDate),
      next: formatShortDate(nextDate),
      isToday: validatedDate === toLocalDateString(new Date())
    };
  };

  const navigationDates = getNavigationDates();

  // Export handler
  const handleExport = async (format: 'excel' | 'csv' | 'pdf' | 'word') => {
    try {
      const response = await api.post(
        '/calendar/export',
        {
        date,
          workspace_id: workspaceId,
          format,
          viewMode: viewMode === 'cards' ? 'card' : 'table' // Map frontend 'cards' to backend 'card'
        },
        {
          responseType: 'blob'
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from response headers or create default
      const disposition = response.headers['content-disposition'];
      const fileExtension = format === 'word' ? 'docx' : format === 'excel' ? 'xlsx' : format;
      let filename = `calendar-${date}.${fileExtension}`;
      
      if (disposition && disposition.includes('filename=')) {
        const filenameMatch = disposition.match(/filename=([^;]+)/);
        if (filenameMatch) {
          filename = filenameMatch[1].replace(/"/g, '');
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Calendar exported as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export calendar');
    }
  };

  // Close export menu when clicking outside
  // Invalid date check - must be after all hooks
  if (!isValidDate) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-text-primary dark:text-text-dark-primary mb-4">Invalid Date</h1>
          <p className="text-text-secondary dark:text-text-dark-secondary">Please provide a valid date in YYYY-MM-DD format.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-text-primary dark:text-text-dark-primary mb-4">Error</h1>
          <p className="text-text-secondary dark:text-text-dark-secondary">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageShell title={workspaceId ? 'Workspace Calendar' : 'Legislative Calendar'}>
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            {/* Date navigation stays — it is navigation, not the page title. */}
            <div className="flex items-center space-x-4">
              {/* Calendar icon - clickable on mobile to open date picker */}
              <button
                onClick={() => setIsDatePickerOpen(true)}
                className="lg:hidden p-1 -m-1 hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary rounded transition-colors"
                title="Pick a date"
              >
                <CalendarIcon className="w-4 h-4 text-text-secondary dark:text-text-dark-secondary" />
              </button>
              <p className="text-text-secondary dark:text-text-dark-secondary">
                {formattedDate}
              </p>
              
              {/* Day Navigation */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={navigateToPreviousDay}
                  className="p-1 hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary rounded transition-colors"
                  title={`Previous Day (${navigationDates.previous})`}
                >
                  <ChevronLeft className="w-4 h-4 text-text-tertiary dark:text-text-dark-tertiary" />
                </button>
                
                <button
                  onClick={navigateToToday}
                  disabled={navigationDates.isToday}
                  className="px-2 py-1 text-xs bg-background-tertiary dark:bg-background-dark-tertiary text-text-secondary dark:text-text-dark-secondary rounded hover:bg-neutral-200 dark:hover:bg-background-dark-quaternary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Go to Today"
                >
                  Today
                </button>
                
                <button
                  onClick={navigateToNextDay}
                  className="p-1 hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary rounded transition-colors"
                  title={`Next Day (${navigationDates.next})`}
                >
                  <ChevronRight className="w-4 h-4 text-text-tertiary dark:text-text-dark-tertiary" />
                </button>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* View Controls Group */}
            <div className="flex items-center gap-2">
              {/* Calendar View Selector */}
              <div className="flex bg-background-tertiary dark:bg-background-dark-tertiary rounded-lg p-1">
                <button
                  onClick={() => {
                    // Navigate to month view for the CURRENT month (today)
                    // Preserve display mode preference (cards/table)
                    const today = new Date();
                    const year = today.getFullYear();
                    const month = today.getMonth() + 1;
                    const displayParam = `&display=${viewMode}`;
                    if (workspaceId) {
                      navigate(`/workspaces/${workspaceId}?tab=calendar&year=${year}&month=${month}${displayParam}`);
                    } else {
                      // Navigate to general calendar month view
                      navigate(`/calendar?year=${year}&month=${month}${displayParam}`);
                    }
                  }}
                  className="px-3 py-1 text-sm rounded-md transition-colors text-text-secondary dark:text-text-dark-secondary hover:text-text-primary dark:hover:text-text-dark-primary"
                >
                  Month
                </button>
                <button
                  onClick={() => {
                    // Navigate to week view for the CURRENT week (today)
                    // Preserve display mode preference (cards/table)
                    const today = new Date();
                    const year = today.getFullYear();
                    const month = today.getMonth() + 1;
                    const day = today.getDate();
                    const displayParam = `&display=${viewMode}`;
                    if (workspaceId) {
                      navigate(`/workspaces/${workspaceId}?tab=calendar&year=${year}&month=${month}&day=${day}&view=week${displayParam}`);
                    } else {
                      // Navigate to general calendar week view
                      navigate(`/calendar?year=${year}&month=${month}&day=${day}&view=week${displayParam}`);
                    }
                  }}
                  className="px-3 py-1 text-sm rounded-md transition-colors text-text-secondary dark:text-text-dark-secondary hover:text-text-primary dark:hover:text-text-dark-primary"
                >
                  Week
                </button>
                <button
                  className="px-3 py-1 text-sm rounded-md transition-colors bg-background-tertiary dark:bg-background-dark-tertiary text-text-primary dark:text-text-dark-primary shadow-sm"
                  disabled
                >
                  Day
                </button>
              </div>

              {/* Display Mode Toggle - hidden on mobile (cards only on mobile) */}
              <div className="hidden lg:flex items-center space-x-2">
                <ViewToggle
                  value={viewMode}
                  onChange={setViewMode}
                  options={[
                    { id: 'cards', label: 'Card View includes all events', icon: LayoutGrid },
                    { id: 'table', label: 'List View includes only bill related events', icon: List },
                  ]}
                />
              </div>

              {/* Share Button */}
              <ShareCalendarDropdown
                date={date}
                workspaceId={workspaceId}
                organizationId={organizationId}
              />

              {/* Export Button */}
              <ExportMenu
                variant="primary"
                formats={
                  viewMode === 'table'
                    ? ['csv', 'excel', { format: 'pdf', note: 'Landscape' }]
                    : [{ format: 'word', note: 'Portrait' }, { format: 'pdf', note: 'Portrait' }]
                }
                onExport={handleExport}
              />
            </div>

            {/* Filters Group */}
            <div className="flex flex-wrap items-center gap-2">
              {/* Chamber Filter */}
              <select
                value={chamberFilter}
                onChange={(e) => setChamberFilter(e.target.value as 'all' | 'senate' | 'house' | 'joint')}
                className="px-3 py-2 border border-border-light dark:border-border-dark rounded-lg bg-white dark:bg-background-dark-secondary text-text-primary dark:text-text-dark-primary focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="all">All Chambers</option>
                <option value="senate">Senate</option>
                <option value="house">House</option>
                <option value="joint">Joint</option>
              </select>

              {/* Search */}
              <SearchField
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search events by title or description..."
                size="sm"
                className="w-48"
              />
            </div>

            {/* Table Controls - Show when in table view */}
            {effectiveViewMode === 'table' && (
              <>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`p-2 rounded-lg transition-colors ${
                    showFilters || hasActiveFilters
                      ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300'
                      : 'bg-background-tertiary dark:bg-background-dark-tertiary text-text-tertiary dark:text-text-dark-tertiary hover:text-text-secondary dark:hover:text-text-dark-secondary'
                  }`}
                  title="Toggle Filters"
                >
                  <Filter className="w-4 h-4" />
                </button>
                
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="p-2 rounded-lg bg-background-tertiary dark:bg-background-dark-tertiary text-text-tertiary dark:text-text-dark-tertiary hover:text-text-secondary dark:hover:text-text-dark-secondary transition-colors"
                    title="Clear filters"
                    aria-label="Clear filters"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Table Filters Panel */}
        {effectiveViewMode === 'table' && showFilters && (
          <div className="card p-4 mb-6">
            <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-4">
              <SearchField
                value={tableFilters.search}
                onChange={(value) => handleFilterChange('search', value)}
                placeholder="Search events by title, bill, or location..."
                size="sm"
                className="w-full sm:min-w-64"
              />
              
              <select
                value={tableFilters.chamber}
                onChange={(e) => handleFilterChange('chamber', e.target.value)}
                className="input"
              >
                <option value="all">All Chambers</option>
                <option value="senate">Senate</option>
                <option value="house">House</option>
                <option value="joint">Joint</option>
              </select>
              
              <select
                value={tableFilters.eventType}
                onChange={(e) => handleFilterChange('eventType', e.target.value)}
                className="input"
              >
                <option value="all">All Event Types</option>
                <option value="committee">Committee</option>
                <option value="chamber">Chamber</option>
              </select>

              <div className="flex items-center space-x-2 sm:ml-auto w-full sm:w-auto justify-between sm:justify-start">
                <span className="text-sm text-text-tertiary dark:text-text-dark-tertiary hidden sm:block">Columns:</span>
                {Object.entries(columnVisibility).map(([key, visible]) => {
                  const columnLabels = {
                    time: 'T',
                    event: 'E', 
                    chamber: 'C',
                    location: 'L',
                    bills: 'B'
                  };
                  return (
                    <button
                      key={key}
                      onClick={() => toggleColumn(key as keyof ColumnVisibility)}
                      className={`p-1 rounded transition-colors relative ${
                        visible 
                          ? 'text-primary-600 dark:text-primary-400' 
                          : 'text-text-tertiary dark:text-text-dark-tertiary'
                      }`}
                      title={`Toggle ${key} column`}
                    >
                      {visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      <span className="absolute -bottom-1 -right-1 text-xs font-bold sm:hidden">
                        {columnLabels[key as keyof typeof columnLabels]}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
            
            {hasActiveFilters && (
              <div className="mt-3 pt-3 border-t border-border-light dark:border-border-dark">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-text-secondary dark:text-text-dark-secondary">
                    {processedEvents?.length || 0} of {dayData?.events?.length || 0} events shown
                  </span>
                  <button
                    onClick={clearFilters}
                    className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                  >
                    Clear all filters
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Layout: Mini-calendar + Content */}
        <div className="lg:grid lg:grid-cols-[18rem_1fr] lg:gap-6 space-y-4 lg:space-y-0">
          {/* Mini-calendar - hidden on mobile */}
          <div className="hidden lg:block">
            <MiniMonthCalendar
              monthDate={miniCalendarMonth}
              selectedDate={validatedDate}
              onSelectDate={handleMiniCalendarSelectDate}
              hasEvent={(dateStr) => datesWithEvents.has(dateStr)}
              onPrevMonth={handleMiniCalendarPrevMonth}
              onNextMonth={handleMiniCalendarNextMonth}
              size="md"
            />
          </div>

          {/* Main content area */}
          <div className="space-y-4">
            {/* Summary Header */}
            {dayData && (
              <CalendarSummaryHeader 
                summary={dayData.summary} 
                trackedBillsCount={trackedBillsCount}
                isWorkspace={!!workspaceId}
              />
            )}

            {/* Content */}
            {dayData ? (
              <div className="mt-4">
                {searchFilteredEvents && searchFilteredEvents.length > 0 ? (
                  <CalendarDayView
                    events={searchFilteredEvents as unknown as CalendarEventDisplay[]}
                    viewMode={effectiveViewMode === 'cards' ? 'card' : 'table'}
                    isPublic={false}
                    workspaceId={workspaceId}
                    onBillClick={handleBillClick}
                    dayData={dayData}
                  />
                ) : (
                  <EmptyState
                    icon={CalendarIcon}
                    title="No events scheduled"
                    hint={
                      searchQuery.trim() || chamberFilter !== 'all'
                        ? `No events found${searchQuery.trim() ? ` matching "${searchQuery.trim()}"` : ''}${chamberFilter !== 'all' ? ` in ${chamberFilter} chamber` : ''}.`
                        : 'No legislative events are scheduled for this date.'
                    }
                  />
                )}
              </div>
            ) : (
              <EmptyState hint="No data available for this date." />
            )}
          </div>
        </div>
      </PageShell>

      {/* Scroll to Top Button */}
      {showScrollToTop && (
        <>
          {/* Mobile: Above bottom navigation */}
          <button
            onClick={scrollToTop}
            className="lg:hidden fixed bottom-20 left-1/2 transform -translate-x-1/2 z-50 p-3 bg-primary-600 hover:bg-primary-700 text-white rounded-full shadow-lg transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            title="Go to top"
          >
            <ArrowUp className="w-4 h-4" />
          </button>
          
          {/* Desktop: Bottom right corner */}
          <button
            onClick={scrollToTop}
            className="hidden lg:block fixed bottom-6 right-6 z-50 p-3 bg-primary-600 hover:bg-primary-700 text-white rounded-full shadow-lg transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            title="Go to top"
          >
            <ArrowUp className="w-5 h-5" />
          </button>
        </>
      )}

      {/* Mobile date picker modal */}
      {isDatePickerOpen && (
        <Modal
          isOpen={true}
          title="Pick a date"
          onClose={() => setIsDatePickerOpen(false)}
          size="sm"
          /* The month grid draws its own edge-to-edge surface. */
          bodyClassName="min-h-0 flex-1 overflow-y-auto"
        >
              <MiniMonthCalendar
                monthDate={miniCalendarMonth}
                selectedDate={validatedDate}
                onSelectDate={(dateStr) => {
                  handleMiniCalendarSelectDate(dateStr);
                  setIsDatePickerOpen(false);
                }}
                hasEvent={(dateStr) => datesWithEvents.has(dateStr)}
                onPrevMonth={handleMiniCalendarPrevMonth}
                onNextMonth={handleMiniCalendarNextMonth}
                showHeader={false}
                size="md"
              />
        </Modal>
      )}
    </>
  );
};

// Summary Header Component
const CalendarSummaryHeader: React.FC<{ 
  summary: CalendarDayData['summary']; 
  trackedBillsCount?: number;
  isWorkspace?: boolean;
}> = ({ summary, trackedBillsCount, isWorkspace }) => {
  return (
    <div className="card">
      <h2 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary mb-4">
        Daily Summary of Scheduled Events
      </h2>
      <StatCardRow>
        <StatCard
          value={isWorkspace && trackedBillsCount !== undefined ? trackedBillsCount : summary.total_bills}
          label={isWorkspace ? 'Tracked Bills' : 'Bills'}
        />
        <StatCard value={summary.senate_chamber_hearings} label="Senate Chamber" variant="info" />
        <StatCard value={summary.senate_committee_hearings} label="Senate Committee" variant="info" />
        <StatCard value={summary.house_chamber_hearings} label="House Chamber" variant="success" />
        <StatCard value={summary.house_committee_hearings} label="House Committee" variant="success" />
        <StatCard value={summary.total_events} label="Total Events" variant="info" />
      </StatCardRow>
    </div>
  );
};

// NOTE: CalendarCardsView and EnhancedCalendarTableView have been replaced by
// the unified CalendarDayView component in components/calendar/CalendarDayView.tsx
// Any changes to calendar event display should be made there.

export default CalendarDetailPage;
